import { useState } from "react";
import { Leaf, Phone, Sparkles, Users } from "lucide-react";
import { MENU_PAGE } from "@/constants/testIds";
import { MENU, RESTAURANT } from "@/data/menu";
import Reveal from "@/components/site/Reveal";

const BANNER = "/images/menu-banner.png";

export default function MenuPage() {
  const [active, setActive] = useState(MENU[0].id);
  const activeCategory = MENU.find((c) => c.id === active);

  return (
    <div data-testid={MENU_PAGE.page}>
      {/* Banner */}
      <section className="relative h-[42vh] min-h-[320px] w-full overflow-hidden flex items-end">
        <div
          className="absolute inset-0 bg-center bg-cover scale-105"
          style={{ backgroundImage: `url(${BANNER})` }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/55 to-black/75" aria-hidden="true" />
        <div className="container-x relative z-10 pb-14">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cream/12 border border-cream/25 text-cream/90 backdrop-blur-sm text-xs tracking-[0.22em] uppercase">
              <Leaf className="w-3.5 h-3.5" /> 100% Pure Vegetarian
            </div>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="mt-5 font-serif text-5xl sm:text-6xl text-cream leading-[1.05]">
              The Menu
            </h1>
          </Reveal>
          <Reveal delay={180}>
            <p className="mt-4 text-cream/80 max-w-xl leading-relaxed">
              Recipes from our kitchen — slow-cooked, generously spiced, and
              plated with care. Prices in INR (₹). All taxes extra.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Tabs */}
      <section className="border-b border-borderc/70 bg-cream sticky top-20 z-30 backdrop-blur-xl bg-cream/85">
        <div
          data-testid={MENU_PAGE.tabs}
          className="container-x flex gap-2 sm:gap-3 overflow-x-auto py-5 scrollbar-none"
          style={{ scrollbarWidth: "none" }}
        >
          {MENU.map((c) => (
            <button
              key={c.id}
              data-testid={`${MENU_PAGE.tab}-${c.id}`}
              onClick={() => setActive(c.id)}
              className={`shrink-0 rounded-full px-5 py-2.5 text-sm font-medium tracking-wide transition-all duration-300 border ${
                active === c.id
                  ? "bg-brand text-cream border-brand shadow-sm"
                  : "bg-white text-ink/75 border-borderc hover:border-brand/40 hover:text-brand"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </section>

      {/* Menu content */}
      <section className="container-x py-16 sm:py-20">
        <Reveal>
          <div className="flex items-baseline justify-between gap-6 flex-wrap mb-10">
            <div>
              <p className="text-xs tracking-[0.24em] uppercase text-brand font-medium">
                {activeCategory.label}
              </p>
              <h2 className="mt-2 font-serif text-3xl sm:text-4xl text-ink">
                {activeCategory.description}
              </h2>
            </div>
            <div className="text-xs text-ink-muted inline-flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-accent2" />
              Jain options available on request
            </div>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 lg:gap-x-16 gap-y-8">
          {activeCategory.items.map((item, i) => (
            <Reveal key={item.name} delay={i * 60}>
              <article
                data-testid={MENU_PAGE.item}
                className="group flex gap-6 items-baseline py-5 border-b border-borderc/60"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-3">
                    <h3
                      data-testid={MENU_PAGE.itemName}
                      className="font-serif text-xl sm:text-2xl text-ink truncate"
                    >
                      {item.name}
                    </h3>
                    <span className="hidden sm:block flex-1 border-b border-dotted border-ink/20 translate-y-[-4px]" />
                    <span
                      data-testid={MENU_PAGE.itemPrice}
                      className="font-serif text-lg sm:text-xl text-brand whitespace-nowrap"
                    >
                      ₹{item.price}
                    </span>
                  </div>
                  <p className="mt-2 text-sm sm:text-base text-ink-muted leading-relaxed pr-6">
                    {item.desc}
                  </p>
                  {item.note && (
                    <p className="mt-2 inline-flex items-center gap-1.5 text-[0.72rem] uppercase tracking-[0.14em] text-accent2/90">
                      <span className="w-1 h-1 rounded-full bg-accent2" /> {item.note}
                    </p>
                  )}
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-16 flex flex-wrap items-center justify-between gap-6 bg-white rounded-3xl border border-borderc/70 px-7 py-7 shadow-sm">
            <div className="flex items-start gap-4 max-w-2xl">
              <span className="grid place-items-center w-12 h-12 rounded-full bg-brand/10 text-brand shrink-0">
                <Users className="w-5 h-5" />
              </span>
              <div>
                <p className="font-serif text-2xl text-ink">
                  Bulk &amp; catering inquiries
                </p>
                <p className="text-sm text-ink-muted mt-1 leading-relaxed">
                  Feeding a crowd? We cater for offices, events &amp; family
                  functions. Call us to discuss.
                </p>
              </div>
            </div>
            <a
              href={`tel:${RESTAURANT.phoneRaw}`}
              data-testid="menu-catering-call-btn"
              className="inline-flex items-center gap-2 rounded-full bg-brand hover:bg-brand-hover text-cream px-7 py-3.5 text-sm font-medium tracking-wide transition-colors"
            >
              <Phone className="w-4 h-4" /> {RESTAURANT.phone}
            </a>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
